import { useCallback, useLayoutEffect, useRef } from 'react';
import { useContinuityDraft } from '@/hooks/useMyCoachContinuity';

type Position = { target: 'window' | number | string; top: number; left: number };
type ReadingPosition = { positions: Position[]; focus: { id: string; start: number | null; end: number | null } | null };

/** Only scoped DOM IDs, offsets and caret ranges enter tab memory. */
export function useMyCoachReadingPosition({ destination, binding, readable, scrollSelector, active=true, restoreOnReadableReentry=false }: {
  destination: string; binding: string | null; readable: boolean; scrollSelector?: string; active?:boolean; restoreOnReadableReentry?:boolean;
}) {
  const root = useRef<HTMLElement | null>(null);
  const draft = useContinuityDraft<ReadingPosition | null>({
    scope: ['reading', destination], binding, readable, baseline: null, equals: () => true,
  });
  const latest = useRef({ draft, readable }); latest.current = { draft, readable };
  const restored = useRef<string | null>(null), deliberate = useRef(false), attempt = useRef(destination), wasActive=useRef(active), wasReadable=useRef(readable);
  if(attempt.current!==destination){attempt.current=destination;restored.current=null;deliberate.current=false;}
  if(wasActive.current!==active){wasActive.current=active;if(active){restored.current=null;deliberate.current=false;}}
  if(wasReadable.current!==readable){wasReadable.current=readable;if(!readable&&restoreOnReadableReentry){restored.current=null;deliberate.current=false;}}
  const ancestors = useCallback(() => {
    const result: HTMLElement[] = [];
    for (let node = root.current?.parentElement; node; node = node.parentElement) {
      if (/auto|scroll/.test(getComputedStyle(node).overflowY)) result.push(node);
    }
    return result;
  }, []);
  const remember = useCallback(() => {
    if (!root.current?.isConnected || !latest.current.readable) return;
    const positions: Position[] = ancestors().map((node, index) => ({ target: index, top: node.scrollTop, left: node.scrollLeft }));
    positions.push({ target: 'window', top: window.scrollY || 0, left: window.scrollX || 0 });
    if (scrollSelector) {
      const node = root.current.querySelector<HTMLElement>(scrollSelector);
      if (node) positions.push({ target: scrollSelector, top: node.scrollTop, left: node.scrollLeft });
    }
    const element = document.activeElement;
    const focus = element instanceof HTMLElement && element.id && root.current.contains(element) ? {
      id: element.id, start: element instanceof HTMLTextAreaElement ? element.selectionStart : null,
      end: element instanceof HTMLTextAreaElement ? element.selectionEnd : null,
    } : null;
    latest.current.draft.update({ positions, focus });
  }, [ancestors, scrollSelector]);
  useLayoutEffect(() => {
    const interacted = () => { deliberate.current = true; };
    window.addEventListener('wheel', interacted, { passive: true });
    window.addEventListener('pointerdown', interacted);
    window.addEventListener('touchstart', interacted, { passive: true });
    window.addEventListener('keydown', interacted);
    window.addEventListener('scroll', remember, true);
    window.addEventListener('focusin', remember);
    window.addEventListener('pointerdown', remember, true);
    window.addEventListener('popstate', remember);
    window.addEventListener('select', remember, true);
    return () => {
      remember();
      window.removeEventListener('wheel', interacted); window.removeEventListener('pointerdown', interacted); window.removeEventListener('touchstart', interacted);
      window.removeEventListener('keydown', interacted); window.removeEventListener('scroll', remember, true);
      window.removeEventListener('focusin', remember); window.removeEventListener('select', remember, true);
      window.removeEventListener('pointerdown', remember, true); window.removeEventListener('popstate', remember);
    };
  }, [remember]);
  useLayoutEffect(() => {
    const key = JSON.stringify([destination, binding]);
    if (!readable) return;
    if (restored.current === key) return;
    restored.current = key;
    const saved = latest.current.draft.value;
    if (!saved || deliberate.current || !root.current) return;
    if(document.activeElement instanceof HTMLElement&&root.current.contains(document.activeElement))return;
    const parents = ancestors();
    for (const position of saved.positions) {
      if (position.target === 'window') {
        if ((window.scrollY || 0) !== position.top || (window.scrollX || 0) !== position.left) window.scrollTo({ top: position.top, left: position.left, behavior: 'auto' });
      } else {
        const node = typeof position.target === 'number' ? parents[position.target] : root.current.querySelector<HTMLElement>(position.target);
        if (node) { node.scrollTop = position.top; node.scrollLeft = position.left; }
      }
    }
    // An explicit destination focus or a deliberate move takes precedence.
    if (saved.focus && document.activeElement === document.body) {
      const element = document.getElementById(saved.focus.id);
      if (element && root.current.contains(element)) {
        element.focus({ preventScroll: true });
        if (element instanceof HTMLTextAreaElement && saved.focus.start !== null) element.setSelectionRange(saved.focus.start, saved.focus.end);
      }
    }
  }, [ancestors, binding, destination, readable]);
  return root;
}
